import { useMemo } from "react";
import { cloneDeep } from "lodash";
import { invariant } from "@searchui-searchforms/shared/utils";
import type { SearchDataModel, Types } from "@/entities/data-model";
import {
    FOCUS_ID_TO_NAME,
    FOCUS_NAME_TO_LOCATIONS,
    FormLevelFeatureType,
    type FormMetadataResponse,
} from "@/entities/form-metadata";
import { useUserPreferencesQuery } from "@/entities/user-preferences";
import { LETTER_TO_TYPE_MAPPING } from "./categoryBucket";
import { prepareGivenNameWithUPS, prepareLastNameWithUPS } from "./userPreferences";

interface UseUserPreferencesParams {
    dataModel: SearchDataModel;
    formMetadata: FormMetadataResponse;
    isEditForm?: boolean;
}

export const useUserPreferences = ({
    dataModel,
    formMetadata,
    isEditForm = false,
}: UseUserPreferencesParams): SearchDataModel => {
    const { data: userPreference } = useUserPreferencesQuery();

    return useMemo(() => {
        if (isEditForm || !userPreference) return dataModel;

        invariant(userPreference, "userPreference is not defined");
        const dataModelToBeUpd = cloneDeep(dataModel);

        const userPreferences = userPreference.UserPreferences;
        if (userPreferences?.FirstNameExactness) {
            const { FirstNameExactness } = userPreferences;
            dataModelToBeUpd.name.givenNameExactness = prepareGivenNameWithUPS(FirstNameExactness);
        }

        if (userPreferences?.LastNameExactness) {
            const { LastNameExactness } = userPreferences;
            dataModelToBeUpd.name.surnameExactness = prepareLastNameWithUPS(LastNameExactness);
        }

        // Collection Focus: 3-layer precedence (URL wins > UPS > DefaultCollectionFocus)
        if (dataModel.priority === "default") {
            const collectionFocusFeature = formMetadata.features.find(
                (f) => f.featureType === FormLevelFeatureType.CollectionFocus,
            );
            if (collectionFocusFeature) {
                const validValues = collectionFocusFeature.attributes.limitedValues.map(
                    (o: { value: string }) => o.value,
                );
                const isValid = (v: string) => v === "default" || validValues.includes(v);

                const upsValue = FOCUS_ID_TO_NAME[userPreferences.CollectionFocus] ?? userPreferences.CollectionFocus;
                const defaultValue =
                    FOCUS_ID_TO_NAME[userPreference.DefaultCollectionFocus] ?? userPreference.DefaultCollectionFocus;

                const chosen =
                    upsValue && isValid(upsValue)
                        ? upsValue
                        : defaultValue && isValid(defaultValue)
                          ? defaultValue
                          : null;

                if (chosen && chosen !== "default") {
                    dataModelToBeUpd.priority = chosen;
                    dataModelToBeUpd.location = FOCUS_NAME_TO_LOCATIONS[chosen] ?? [];
                }
            }
        }

        const categoryBucketPref = userPreferences.CategoryBucket;
        const categoryBucketFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.CategoryBuckets,
        );
        if (categoryBucketPref && categoryBucketFeature) {
            dataModelToBeUpd.types = {
                records: false,
                photos: false,
                stories: false,
                trees: false,
            };
            categoryBucketPref.split("").forEach((letter) => {
                const typeKey = LETTER_TO_TYPE_MAPPING[letter.toLowerCase()];
                if (typeKey && categoryBucketFeature.attributes.buckets[typeKey]?.show) {
                    dataModelToBeUpd.types[typeKey as keyof Types] = true;
                }
            });
        }

        const showUnviewedRecordsOnlyPref = userPreferences.ShowUnviewedRecordsOnly;
        const showUnviewedRecordsOnlyFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.ShowUnviewedRecordsOnly,
        );
        if (showUnviewedRecordsOnlyPref && showUnviewedRecordsOnlyFeature) {
            dataModelToBeUpd.showUnviewedRecordsOnly = showUnviewedRecordsOnlyPref;
        }
        return dataModelToBeUpd;
    }, [dataModel, formMetadata, isEditForm, userPreference]);
};
